import React, { ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface PermissionGateProps {
  children: ReactNode;
  allowedRoles?: ('admin' | 'staff')[];
  requireAdmin?: boolean;
  fallback?: ReactNode;
}

/**
 * Component to conditionally render children based on user role/permissions
 *
 * Usage:
 * <PermissionGate requireAdmin>
 *   <AdminOnlyComponent />
 * </PermissionGate>
 *
 * <PermissionGate allowedRoles={['admin', 'staff']}>
 *   <Component />
 * </PermissionGate>
 */
export default function PermissionGate({
  children,
  allowedRoles,
  requireAdmin = false,
  fallback = null
}: PermissionGateProps) {
  const { user, role, isAdmin } = useAuth();

  // If requireAdmin is set, check for admin role
  if (requireAdmin && !isAdmin) {
    return <>{fallback}</>;
  }

  // If allowedRoles is specified, check if user's role is in the list
  if (allowedRoles && role && !allowedRoles.includes(role)) {
    return <>{fallback}</>;
  }

  // If no specific requirements, or all checks pass, render children
  return <>{children}</>;
}

/**
 * Hook to check permissions
 */
export function usePermission() {
  const { role, isAdmin, isStaff } = useAuth();

  const hasRole = (requiredRole: 'admin' | 'staff') => {
    return role === requiredRole;
  };

  const canDelete = () => isAdmin;
  const canEdit = () => isAdmin || isStaff;
  const canCreate = () => isAdmin || isStaff;
  const canBulkOperation = () => isAdmin;

  return {
    role,
    isAdmin,
    isStaff,
    hasRole,
    canDelete,
    canEdit,
    canCreate,
    canBulkOperation
  };
}